import React, { useCallback, useEffect, useState } from 'react';
import { CacheStats, CacheStatsResponse } from '../../../core/api/IApiClient';
import { useRestClient } from '../../../core/api/RestClientContext';
import { Badge } from '../../../core/components/Badge';
import { Button } from '../../../core/components/Button';
import './CacheManagementPanel.css';

const formatTtl = (seconds: number): string => {
    if (seconds >= 3600) return `${(seconds / 3600).toFixed(0)}ч`;
    if (seconds >= 60)   return `${(seconds / 60).toFixed(0)}м`;
    return `${seconds}с`;
};

export const CacheManagementPanel: React.FC = () => {
    const restClient = useRestClient();
    const [data, setData] = useState<CacheStatsResponse | null>(null);
    const [flushing, setFlushing] = useState<string | 'all' | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const stats = await restClient.getCacheStats();
            setData(stats);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [restClient]);

    useEffect(() => { load(); }, [load]);

    const handleFlushAll = async () => {
        setFlushing('all');
        try {
            await restClient.flushAllCaches();
            await load();
        } finally {
            setFlushing(null);
        }
    };

    const handleFlush = async (name: string) => {
        setFlushing(name);
        try {
            await restClient.flushCache(name);
            await load();
        } finally {
            setFlushing(null);
        }
    };

    return (
        <div className="cu-card cu-card--default cache-panel">
            <div className="cache-panel__header">
                <h3 className="cache-panel__title">Redis Cache</h3>
                <Button
                    variant="primary"
                    onClick={handleFlushAll}
                    disabled={flushing !== null || !data}
                >
                    {flushing === 'all' ? 'Сброс…' : 'Сбросить все'}
                </Button>
            </div>

            {data && (
                <div className="cache-panel__redis-info">
                    <span title="Используемая память">
                        Память: <strong>{data.redis.usedMemory}</strong>
                    </span>
                    <span title="Пик использования памяти">
                        Пик: <strong>{data.redis.usedMemoryPeak}</strong>
                    </span>
                    <span title="RSS память">
                        RSS: <strong>{data.redis.usedMemoryRss}</strong>
                    </span>
                    <span title="Подключённых клиентов">
                        Клиенты: <strong>{data.redis.connectedClients}</strong>
                    </span>
                    <span title="Версия Redis" className="cache-panel__redis-version">
                        v{data.redis.redisVersion}
                    </span>
                </div>
            )}

            {loading && <div className="cache-panel__loading">Загрузка…</div>}

            {!loading && data && (
                <ul className="cache-panel__list">
                    {data.caches.map((c: CacheStats) => (
                        <li key={c.name} className="cache-panel__item">
                            <div className="cache-panel__item-info">
                                <span className="cache-panel__name">{c.name}</span>
                                <div className="cache-panel__badges">
                                    <Badge variant="default" title="Кол-во объектов">
                                        {c.entryCount} {c.entryCount === 1 ? 'запись' : 'записей'}
                                    </Badge>
                                    <Badge variant="info" title="TTL">
                                        TTL {formatTtl(c.ttlSeconds)}
                                    </Badge>
                                </div>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => handleFlush(c.name)}
                                disabled={flushing !== null}
                            >
                                {flushing === c.name ? 'Сброс…' : 'Сбросить'}
                            </Button>
                        </li>
                    ))}
                    {data.caches.length === 0 && (
                        <li className="cache-panel__empty">Нет активных кэшей</li>
                    )}
                </ul>
            )}

            {!loading && !data && (
                <div className="cache-panel__empty">Не удалось загрузить данные</div>
            )}
        </div>
    );
};
